/* eslint-disable react/no-array-index-key */
/* eslint-disable no-console */
import React, { useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { IconExportExcel } from '@assets/icons';
import { CHART_COLORS, downloadChartAsImage } from '@common/constant/chart';
import { YEAR_MONTH_FORMAT } from '@common/constant/date';
import { getDateRangeSuffix } from '@common/utils/dateHelper';
import durationToTimeFormat from '@common/utils/convertDuration';
import { fabAccessLogApi } from '@services/fabAccessLogApi';
import '@styles/pages/AnalyticDetail.scss';
import { saveAs } from 'file-saver';
import { t } from 'i18next';
import PropTypes from 'prop-types';
import ComLoading from '@components/common/ComLoading';
import { GetMonthStatus } from '@common/utils/optionFilterHelper';
import { isMonthPickerMode } from '@common/utils/modeHelper';
import { Tooltip } from 'antd';

function PieChartModal({
  isVisible,
  onClose,
  chartData,
  isDurationChart = false,
  isLoading = false,
  pagination,
  pickerMode,
  layoutName,
  monthOptions = [],
}) {
  const chartRef = useRef(null);

  const GetFromDateWithStatus = useMemo(() => {
    if (monthOptions.length > 0 && pagination.from_date) {
      const fromStatus = GetMonthStatus(monthOptions, pagination.from_date);
      return `${dayjs(pagination.from_date).format(
        YEAR_MONTH_FORMAT,
      )}（${fromStatus}）`;
    }
    return '----/--（--）';
  }, [monthOptions, pagination.from_date]);

  const GetToDateWithStatus = useMemo(() => {
    if (monthOptions.length > 0 && pagination.to_date) {
      const toStatus = GetMonthStatus(monthOptions, pagination.to_date);
      return `${dayjs(pagination.to_date).format(
        YEAR_MONTH_FORMAT,
      )}（${toStatus}）`;
    }
    return '----/--（--）';
  }, [monthOptions, pagination.to_date]);

  if (!isVisible) return null;

  const exportExcelFile = async (nameFile) => {
    try {
      const fileData = await fabAccessLogApi.exportSummaryAccessLogs(
        pagination,
      );
      const blob = new Blob([fileData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `${nameFile}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const exportCSVAndDownloadChart = async () => {
    const suffix = getDateRangeSuffix(
      pagination.from_date,
      pagination.to_date,
      pickerMode,
      'YYYYMM',
    );

    const nameFile = isDurationChart
      ? `FaView_分析_${layoutName}_総プレイ時間_${suffix}`
      : `FaView_分析_${layoutName}_アクセス数_${suffix}`;
    await exportExcelFile(nameFile);
    await downloadChartAsImage(chartRef, `${nameFile}.png`);
  };

  // Calculate total for percentage calculation
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  // Calculate angles for pie chart
  let currentAngle = -90; // Start from top
  const segments = chartData.map((item, index) => {
    const percentage = (item.value / total) * 100;
    let angle = (item.value / total) * 360;

    // For single item (100%), reduce angle slightly to avoid overlapping points
    if (chartData.length === 1) {
      angle = 359.99;
    }

    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    // Calculate path for donut segment
    const radius = 150;
    const innerRadius = 121;
    const centerX = 250;
    const centerY = 200;

    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;

    const x1 = centerX + radius * Math.cos(startAngleRad);
    const y1 = centerY + radius * Math.sin(startAngleRad);
    const x2 = centerX + radius * Math.cos(endAngleRad);
    const y2 = centerY + radius * Math.sin(endAngleRad);
    const x3 = centerX + innerRadius * Math.cos(endAngleRad);
    const y3 = centerY + innerRadius * Math.sin(endAngleRad);
    const x4 = centerX + innerRadius * Math.cos(startAngleRad);
    const y4 = centerY + innerRadius * Math.sin(startAngleRad);

    const largeArc = angle > 180 ? 1 : 0;

    const path = `
      M ${x1} ${y1}
      A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
      L ${x3} ${y3}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
      Z
    `;

    // Calculate label position
    const labelAngle = (startAngle + endAngle) / 2;
    const labelAngleRad = (labelAngle * Math.PI) / 180;
    const labelRadius = radius + 30;
    const labelX = centerX + labelRadius * Math.cos(labelAngleRad);
    const labelY = centerY + labelRadius * Math.sin(labelAngleRad);

    // Get color from palette - use direct index for distinct colors
    // Top 1 (index 0) gets CHART_COLORS[0], Top 2 (index 1) gets CHART_COLORS[1], etc.
    const color = CHART_COLORS[index % CHART_COLORS.length];

    return {
      path,
      color,
      percentage: percentage.toFixed(1),
      labelX,
      labelY,
      item,
      startAngle,
      endAngle,
    };
  });

  return (
    <div className="chart">
      <div className="chart-modal-overlay" onClick={onClose}>
        <div
          ref={chartRef}
          className="chart-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          {isLoading ? (
            <ComLoading type="loading" />
          ) : (
            <div>
              {chartData.length > 0 && (
                <>
                  <div className="chart-header">
                    <h3>{`${layoutName} ${
                      isMonthPickerMode(pickerMode)
                        ? GetFromDateWithStatus
                        : `${GetFromDateWithStatus} ~ ${GetToDateWithStatus}`
                    }`}</h3>
                    <div className="chart-header-actions">
                      <IconExportExcel
                        className="pointer"
                        onClick={exportCSVAndDownloadChart}
                      />
                      <div className="close-btn" onClick={onClose}>
                        ×
                      </div>
                    </div>
                  </div>
                  <div className="pie-chart-container">
                    <div className="pie-chart-content">
                      <svg width="100%" height="100%" viewBox="0 0 500 400">
                        {/* Pie segments */}
                        {segments.map((segment, index) => {
                          const midAngle =
                            (segment.startAngle + segment.endAngle) / 2;
                          const normalizedAngle =
                            ((midAngle % 360) + 360) % 360;

                          // Determine best placement based on angle (more precise)
                          let placement = 'top';
                          if (normalizedAngle >= 0 && normalizedAngle < 45) {
                            placement = 'right';
                          } else if (
                            normalizedAngle >= 45 &&
                            normalizedAngle < 135
                          ) {
                            placement = 'bottom';
                          } else if (
                            normalizedAngle >= 135 &&
                            normalizedAngle < 225
                          ) {
                            placement = 'left';
                          } else if (
                            normalizedAngle >= 225 &&
                            normalizedAngle < 315
                          ) {
                            placement = 'top';
                          } else {
                            placement = 'right';
                          }

                          return (
                            <Tooltip
                              key={`segment-${index}`}
                              title={
                                <div>
                                  <div>
                                    <strong>{segment.item.name}</strong>
                                  </div>
                                  <div>
                                    {isDurationChart
                                      ? durationToTimeFormat(segment.item.value)
                                      : `${segment.item.value}回`}
                                  </div>
                                </div>
                              }
                              placement={placement}
                            >
                              <g>
                                <path
                                  d={segment.path}
                                  fill={segment.color}
                                  style={{ cursor: 'pointer' }}
                                />
                              </g>
                            </Tooltip>
                          );
                        })}

                        {/* Labels - hide labels for segments < 3% */}
                        {segments.map((segment, index) => {
                          const showLabel = segment.percentage >= 3;
                          if (!showLabel) return null;

                          return (
                            <g key={`label-${index}`}>
                              <text
                                x={segment.labelX}
                                y={segment.labelY - 8}
                                textAnchor="middle"
                                fontSize="10"
                                fontWeight="bold"
                                fill="#333"
                              >
                                {segment.item.name}
                              </text>
                              <text
                                x={segment.labelX}
                                y={segment.labelY + 8}
                                textAnchor="middle"
                                fontSize="16"
                                fill="#333"
                              >
                                {isDurationChart
                                  ? durationToTimeFormat(segment.item.value)
                                  : `${segment.item.value}回`}
                              </text>
                            </g>
                          );
                        })}

                        {/* Center text */}
                        <text
                          x="250"
                          y="185"
                          textAnchor="middle"
                          fontSize="16"
                          fontWeight="bold"
                          fill="#333"
                        >
                          {isDurationChart
                            ? t('analytic.detail.table.totalDuration')
                            : t('analytic.detail.table.accessCount')}
                        </text>
                        <text
                          x="250"
                          y="215"
                          textAnchor="middle"
                          fontSize="32"
                          fontWeight="bold"
                          fill="#333"
                        >
                          {isDurationChart
                            ? durationToTimeFormat(total)
                            : total}
                        </text>
                      </svg>
                    </div>
                    <div className="chart-table">
                      <div className="table-header">
                        <div className="header-item">
                          {t('analytic.detail.filter.nameCode')}
                        </div>
                        <div className="header-item">
                          {isDurationChart
                            ? t('analytic.detail.table.totalDuration')
                            : t('analytic.detail.table.accessCount')}
                        </div>
                      </div>
                      {chartData.map((item, index) => (
                        <div
                          key={`table-${item.name}-${index}`}
                          className="table-row"
                        >
                          <div className="table-cell">{item.name}</div>
                          <div className="table-cell">
                            {isDurationChart
                              ? durationToTimeFormat(item.value)
                              : item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

PieChartModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  isDurationChart: PropTypes.bool,
  isLoading: PropTypes.bool,
  pagination: PropTypes.object,
  pickerMode: PropTypes.string,
  layoutName: PropTypes.string,
  monthOptions: PropTypes.any,
};

export default PieChartModal;
